import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShoppingBag, Plus, Pencil, Trash2, Loader2, X, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminServicesCatalogPage = () => {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'vuelos', description: '', base_price: '', currency: 'USD', commission_pct: '0', profit_pct: '0' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/services-catalog`, { headers: getAuthHeaders() });
      setServices(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ name: '', category: 'vuelos', description: '', base_price: '', currency: 'USD', commission_pct: '0', profit_pct: '0' }); setEditing(null); setShowForm(false); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, base_price: form.base_price ? parseFloat(form.base_price) : null, commission_pct: parseFloat(form.commission_pct), profit_pct: parseFloat(form.profit_pct) };
    try {
      if (editing) {
        await axios.put(`${API}/services-catalog/${editing}`, payload, { headers: getAuthHeaders() });
        toast.success('Servicio actualizado');
      } else {
        await axios.post(`${API}/services-catalog`, payload, { headers: getAuthHeaders() });
        toast.success('Servicio creado');
      }
      resetForm();
      load();
    } catch (e) { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este servicio?')) return;
    try {
      await axios.delete(`${API}/services-catalog/${id}`, { headers: getAuthHeaders() });
      toast.success('Eliminado');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const toggleActive = async (s) => {
    try {
      await axios.put(`${API}/services-catalog/${s.id}`, { is_active: !s.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const startEdit = (s) => {
    setForm({ name: s.name, category: s.category, description: s.description || '', base_price: s.base_price?.toString() || '', currency: s.currency, commission_pct: s.commission_pct?.toString() || '0', profit_pct: s.profit_pct?.toString() || '0' });
    setEditing(s.id);
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const categories = ['vuelos', 'remesas', 'recargas', 'paquetes', 'otros'];

  return (
    <div className="space-y-6" data-testid="admin-services-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catalogo de Servicios</h1>
          <p className="text-slate-500">Gestiona los servicios disponibles para clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-blue-200 bg-blue-50" data-testid="service-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{editing ? 'Editar' : 'Nuevo'} Servicio</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vuelos internacionales" required data-testid="svc-name" />
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="svc-category">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Descripcion</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descripcion del servicio" data-testid="svc-desc" />
            </div>
            <div className="space-y-2">
              <Label>Precio base (USD)</Label>
              <Input type="number" step="0.01" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} placeholder="100.00" data-testid="svc-price" />
            </div>
            <div className="space-y-2">
              <Label>Moneda</Label>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="svc-currency" />
            </div>
            <div className="space-y-2">
              <Label>Comision %</Label>
              <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} data-testid="svc-commission" />
            </div>
            <div className="space-y-2">
              <Label>Ganancia %</Label>
              <Input type="number" step="0.1" value={form.profit_pct} onChange={(e) => setForm({ ...form, profit_pct: e.target.value })} data-testid="svc-profit" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="svc-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {services.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay servicios configurados</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {services.map((s) => (
            <Card key={s.id} className={`p-4 border-0 shadow-sm ${!s.is_active ? 'opacity-50' : ''}`} data-testid={`svc-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{s.name}</h3>
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{s.category}</span>
                  {s.description && <p className="text-sm text-slate-500 mt-1">{s.description}</p>}
                  <div className="flex gap-4 mt-2 text-xs text-slate-600">
                    {s.base_price && <span>Base: ${s.base_price}</span>}
                    <span>Comision: {s.commission_pct}%</span>
                    <span>Ganancia: {s.profit_pct}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleActive(s)} className="p-1">
                    {s.is_active ? <ToggleRight className="w-6 h-6 text-green-500" /> : <ToggleLeft className="w-6 h-6 text-slate-400" />}
                  </button>
                  <button onClick={() => startEdit(s)} className="p-1 text-blue-500"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(s.id)} className="p-1 text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminServicesCatalogPage;
